import { useTranslation } from "react-i18next";
import { SectionMessagesWrapper } from "./styles";
import { useDM } from "@services";
import { Dispatch, Fragment, SetStateAction, useCallback, useRef, useEffect } from "react";
import AirbnbIcon from "@assets/svg/airbnb.svg?react";
import GathernIcon from "@assets/svg/GathernIcon2.svg?react";
import BookingIcon from "@assets/svg/BookingIcon.svg?react";
import ChannelAgoda from "@assets/svg/ChannelAgoda.svg?react";
import MsoolIcon from "@assets/svg/msool-icon.svg?react";
import dayjs from "dayjs";
import { Typography, Box, CircularProgress } from "@mui/material";

interface IProps {
  setSelectedMessageId: Dispatch<SetStateAction<string | undefined>>;
  selectedMessageId: string | undefined;
}
export default function SectionMessages({
  setSelectedMessageId,
  selectedMessageId,
}: IProps) {
  const channals = {
    airbnb: <AirbnbIcon />,
    gathern: <GathernIcon />,
    bookingcom: <BookingIcon />,
    agoda: <ChannelAgoda />,
    msool: <MsoolIcon />,
  };
  const { GetAllMessageThreadsInfiniteQuery } = useDM();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = GetAllMessageThreadsInfiniteQuery();

  const { t } = useTranslation();
  const observerTarget = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);

  // Flatten all pages into single array
  const allMessages = data?.pages.flatMap((page) => page.data.result) ?? [];

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (
          target.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isFetchingRef.current
        ) {
          isFetchingRef.current = true;
          fetchNextPage().finally(() => {
            isFetchingRef.current = false;
          });
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <SectionMessagesWrapper>
      <p>{t("DM.MESSAGES")}</p>
      <div className="containerList">
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
            }}
          >
            <CircularProgress size={30} />
          </Box>
        ) : allMessages.length === 0 ? (
          <div className="containerNoData">
            <p className="noData">{t("DM.NOMESSAGES")}</p>
          </div>
        ) : (
          <Fragment>
            {allMessages.map((item) => {
              const isActive = item.id === selectedMessageId;
              return (
                <div
                  className={`${isActive ? "active" : "containerCardMessageInfo"}`}
                  key={item.id}
                  onClick={() => setSelectedMessageId(item.id)}
                >
                  <div className="containerIcon">
                    <div>
                      {
                        channals[
                          item.attributes.provider.toLowerCase() as
                            | "airbnb"
                            | "gathern"
                            | "bookingcom"
                            | "agoda"
                            | "msool"
                        ]
                      }
                    </div>
                  </div>
                  <div className="containerDataInfo">
                    <Typography
                      sx={{
                        color: "#000000",
                        fontWeight: 700,
                        opacity: isActive ? 1 : 0.7,
                        lineHeight: "20px",
                        fontSize: "14px",
                      }}
                    >
                      {item.attributes.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#000000",
                        fontWeight: 400,
                        lineHeight: "16px",
                        fontSize: "12px",
                        opacity: 0.4,
                      }}
                    >
                      {item?.attributes?.last_message?.message}
                    </Typography>
                  </div>
                  <div className="containerTime">
                    <p>
                      {item?.attributes?.last_message_received_at
                        ? (() => {
                            const messageDate = dayjs(item.attributes.last_message_received_at);
                            const now = dayjs();
                            
                            // Today: show time only
                            if (messageDate.isSame(now, 'day')) {
                              return messageDate.format("HH:mm");
                            }
                            
                            // Same week: show day name and time
                            if (messageDate.isSame(now, 'week')) {
                              return messageDate.format("dddd HH:mm");
                            }
                            
                            // Same year (different week/month): show month name and day with time
                            if (messageDate.isSame(now, 'year')) {
                              return messageDate.format("MMM D HH:mm");
                            }
                            
                            // Different year: show full date and time
                            return messageDate.format("MMM D, YYYY HH:mm");
                          })()
                        : null}
                    </p>
                  </div>
                </div>
              );
            })}
            {/* Intersection Observer Target */}
            <div
              ref={observerTarget}
              style={{
                height: "20px",
                margin: "10px 0",
              }}
            />
            {/* Loading indicator for next page */}
            {isFetchingNextPage && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "10px",
                  gap: "4px",
                }}
              >
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#1976d2",
                    animation: "bounce 1.4s infinite ease-in-out both",
                    animationDelay: "-0.32s",
                    "@keyframes bounce": {
                      "0%, 80%, 100%": {
                        transform: "scale(0)",
                      },
                      "40%": {
                        transform: "scale(1)",
                      },
                    },
                  }}
                />
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#1976d2",
                    animation: "bounce 1.4s infinite ease-in-out both",
                    animationDelay: "-0.16s",
                    "@keyframes bounce": {
                      "0%, 80%, 100%": {
                        transform: "scale(0)",
                      },
                      "40%": {
                        transform: "scale(1)",
                      },
                    },
                  }}
                />
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "#1976d2",
                    animation: "bounce 1.4s infinite ease-in-out both",
                    "@keyframes bounce": {
                      "0%, 80%, 100%": {
                        transform: "scale(0)",
                      },
                      "40%": {
                        transform: "scale(1)",
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Fragment>
        )}
      </div>
    </SectionMessagesWrapper>
  );
}
